/**
 * UN-DOLLAR RENTFLOW — ADMIN PORTAL ENTRY POINT
 * 
 * Separate entry point for internal admin portal.
 * Only includes admin-facing routes and features.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ChakraProvider } from '@chakra-ui/react';
import { AuthProvider } from './contexts/AuthContext';
import App from './App';
import theme from './theme';
import './i18n';
import { seedTestLeads, deleteTestLeads } from './utils/seedTestLeads';
import { auth, db } from './services/firebase';
import { collection, getDocs, doc, updateDoc, Timestamp } from 'firebase/firestore';

// Expose test data functions to browser console for development
if (import.meta.env.DEV) {
  (window as any).seedTestData = async (userId?: string) => {
    const user = auth.currentUser;
    if (!user && !userId) {
      console.error('❌ No authenticated user. Please login first or provide userId.');
      return;
    }
    
    const uid = userId || user!.uid;
    const name = user?.displayName || 'Test Admin';
    
    console.log('🌱 Seeding test data for user:', uid);
    const count = await seedTestLeads(uid, name);
    console.log(`✅ Created ${count} test leads`);
    return count;
  };
  
  (window as any).deleteTestData = async () => {
    console.log('🗑️ Deleting test data...');
    const count = await deleteTestLeads();
    console.log(`✅ Deleted ${count} test leads`);
    return count;
  };
  
  // Mark first lead as closed_won to enable onboarding creation
  (window as any).markLeadAsWon = async () => {
    try {
      const leadsSnapshot = await getDocs(collection(db, 'leads'));
      if (leadsSnapshot.empty) {
        console.log('❌ No leads found. Run seedTestData() first.');
        return;
      }
      
      const firstLead = leadsSnapshot.docs[0];
      await updateDoc(doc(db, 'leads', firstLead.id), {
        status: 'closed_won',
        closedAt: Timestamp.now(),
        closedReason: 'Customer signed contract'
      });
      
      console.log('✅ Lead marked as closed_won:', firstLead.id);
      console.log('👉 Refresh the page and go to Onboarding to create an onboarding');
      return firstLead.id;
    } catch (error) {
      console.error('❌ Error:', error);
    }
  };

  (window as any).checkPermissions = async () => {
    const user = auth.currentUser;
    if (!user) {
      console.error('❌ No authenticated user');
      return;
    }
    
    console.log('🔍 Checking permissions...');
    console.log('User UID:', user.uid);
    console.log('User Email:', user.email);
    
    // Try to read from users collection
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        console.log('✅ Can read from users collection');
        console.log('User data:', userDoc.data());
      } else {
        console.log('⚠️ No document in users collection');
      }
    } catch (error) {
      console.error('❌ Cannot read from users collection:', error);
    }
    
    // Try to read from leads collection
    try {
      const { collection, getDocs, query, limit } = await import('firebase/firestore');
      const leadsQuery = query(collection(db, 'leads'), limit(1));
      const snapshot = await getDocs(leadsQuery);
      console.log(`✅ Can read from leads collection (${snapshot.size} documents)`);
    } catch (error) {
      console.error('❌ Cannot read from leads collection:', error);
    }
  };
  
  // Test all collections helper
  (window as any).testAllCollections = async () => {
    console.log('🔍 Testing all collections...');
    const collections = [
      'leads', 
      'proposals', 
      'contracts', 
      'settings', 
      'companySettings', 
      'landlordOnboardings',
      'onboardings',
      'users',
      'landlords',
      'emailTemplates',
      'auditLogs',
    ];
    
    const results: any = { passed: [], failed: [] };
    
    for (const col of collections) {
      try {
        const snapshot = await getDocs(collection(db, col));
        console.log(`✅ ${col}: ${snapshot.size} documents`);
        results.passed.push({ collection: col, count: snapshot.size });
      } catch (error: any) {
        console.error(`❌ ${col}:`, error.message);
        results.failed.push({ collection: col, error: error.message });
      }
    }
    
    console.log('\n📊 Summary:');
    console.log(`✅ Passed: ${results.passed.length}/${collections.length}`);
    console.log(`❌ Failed: ${results.failed.length}/${collections.length}`);
    
    if (results.failed.length > 0) {
      console.log('\n⚠️ Failed collections need permission fixes:');
      results.failed.forEach((f: any) => console.log(`  - ${f.collection}: ${f.error}`));
    }
    
    return results;
  };
  
  // Auto-run permission check on page load in development
  auth.onAuthStateChanged(async (user) => {
    if (user && import.meta.env.DEV) {
      console.log('\n🔐 Running automatic permission check...');
      setTimeout(async () => {
        await (window as any).testAllCollections();
      }, 2000); // Wait 2 seconds for Firebase to initialize
    }
  });
  
  console.log('🔧 Development helpers available:');
  console.log('  - seedTestData(userId?) - Create test leads');
  console.log('  - deleteTestData() - Delete test leads');
  console.log('  - markLeadAsWon() - Mark first lead as closed_won');
  console.log('  - checkPermissions() - Check Firestore access');
  console.log('  - testAllCollections() - Test all collection permissions');
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ChakraProvider theme={theme}>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </ChakraProvider>
  </React.StrictMode>
);
